/** @jsx jsx */

import { isEmpty } from 'lodash';

import { jsx } from '@emotion/core';
import {
  EditorPreview as EditorPreviewForm,
  humanize,
} from '@zapier/editor-core';
import { Badge, Spacer, Spinner, Tag, Text } from '@zapier/design-system';

import { APP_STATUSES_NAME } from 'app/developer-v3/constants/status';
import {
  useQueryServiceImplementationMeta,
  useZapUrl,
} from 'app/developer-v3/hooks';

import ServiceIcon from '../ServiceIcon';
import { Styles } from './EditorPreview.styles';
import { convertFormFields, getDefaultValues } from './utils';

import type { AppService } from 'app/developer-v3/types';
import type { FieldOrFunctionSchema } from 'app/developer-v3/platformSchema/field';

type Props = {
  appLogoUrl?: string;
  appName: string;
  appStatus: APP_STATUSES_NAME;
  inputFields: Array<FieldOrFunctionSchema>;
  selectedApi: string;
  service: AppService;
};

const EditorPreview = ({
  appName,
  appLogoUrl,
  appStatus,
  inputFields,
  selectedApi,
  service,
}: Props) => {
  const status = [APP_STATUSES_NAME.public, APP_STATUSES_NAME.beta].includes(
    appStatus
  )
    ? 'public'
    : 'private';
  const {
    isLoading: isLoadingServiceImplementation,
    data,
  } = useQueryServiceImplementationMeta({ selectedApi, status });

  const getZapUrl = useZapUrl();
  const zapUrl = getZapUrl() || '';

  const defaultValues = getDefaultValues(inputFields);

  return (
    <aside css={Styles.preview}>
      <header css={Styles.header}>
        <Badge>ZAP EDITOR PREVIEW</Badge>
        {isEmpty(inputFields) ? (
          <Text margin="20px 0px 0px">
            Get a preview of your app here as you build it in the Form Editor
          </Text>
        ) : (
          <div css={Styles.logo}>
            <ServiceIcon serviceName={appName} size={80} src={appLogoUrl} />
            <Spacer height={10} />
            {isLoadingServiceImplementation ? (
              <Spinner />
            ) : data?.banner ? (
              <Tag variant={data.isBeta ? 'default' : 'inverted'}>
                {data.banner}
              </Tag>
            ) : null}
            <Spacer height="1em" />
            <Text type="Body1">
              Set up {appName} {humanize(service.noun)}
            </Text>
          </div>
        )}
      </header>
      <EditorPreviewForm
        fields={convertFormFields(zapUrl, inputFields)}
        selectedApi={selectedApi}
        value={defaultValues}
      />
    </aside>
  );
};

EditorPreview.displayName = 'EditorPreview';

export default EditorPreview;
